import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { useAuth } from "../../../software/AuthContext";
import { toast } from "sonner";
import { printEndOfDayOpenTill, printPosEndOfDay } from "../reports/legacy/docs";

// Replica of Form_POSSummaryPrints (END OF DAY). Positions are the Access twips / 15.
const DENOMS = [["100", "$100.00", 100], ["50", "$50.00", 50], ["20", "$20.00", 20], ["10", "$10.00", 10], ["5", "$5.00", 5], ["2", "$2.00", 2],
  ["1", "$1.00", 1], ["0.5", "$0.50c", 0.5], ["0.2", "$0.20c", 0.2], ["0.1", "$0.10c", 0.1], ["0.05", "$0.05c", 0.05]];
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const auToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
const dmy = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const T = (l, t, w, h) => ({ position: "absolute", left: l / 15, top: t / 15, width: w / 15, height: h / 15 });

function Ladder({ qty, setQty, onLast }) {
  return (
    <div className="absolute left-0 top-0 bottom-0 w-[112px] bg-black/70 px-1 py-1.5 space-y-[3px]" data-testid="eod-denoms">
      {DENOMS.map(([k, label, v], i) => (
        <div key={k} className="leading-none">
          <div className="flex items-center justify-between gap-1">
            <span className="text-white font-black text-[12.5px]">{label}</span>
            <input value={qty[k] || ""} inputMode="numeric" data-testid={`eod-qty-${k}`}
              onChange={(e) => setQty(k, e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); i === DENOMS.length - 1 ? onLast() : document.querySelector(`[data-testid="eod-qty-${DENOMS[i + 1][0]}"]`)?.focus(); } }}
              className="w-[30px] bg-[#fff7b0] border border-black/50 text-[12px] text-center outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div className="text-right text-amber-300 underline text-[11px] pr-0.5" data-testid={`eod-line-${k}`}>{money((Number(qty[k]) || 0) * v)}</div>
        </div>
      ))}
      <div className="border-t border-white/40 pt-1 text-right text-white font-black text-[12.5px]" data-testid="eod-denom-total">
        {money(DENOMS.reduce((s, [k, , v]) => s + (Number(qty[k]) || 0) * v, 0))}
      </div>
    </div>
  );
}

const Btn = ({ pos, onClick, testid, children }) => (
  <button style={pos} onClick={onClick} data-testid={testid}
    className="rounded-md bg-gradient-to-b from-[#2f6db5] to-[#173f73] border border-white/60 text-white font-black text-[15px] tracking-wide shadow-lg hover:from-[#3b80cf] transition-colors">{children}</button>
);

export default function EndOfDay({ clerk, company, onClose }) {
  const { logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [qty, setQtyState] = useState({});
  const [eod, setEod] = useState("");
  const [range, setRange] = useState({ start: auToday(), end: auToday() });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("GET", "/pos/end-of-day").then(setInfo).catch((e) => toast.error(e.message)); }, []);
  const denomTotal = useMemo(() => DENOMS.reduce((s, [k, , v]) => s + (Number(qty[k]) || 0) * v, 0), [qty]);
  const setQty = (k, v) => { const n = { ...qty, [k]: v }; setQtyState(n); setEod(DENOMS.reduce((s, [d, , x]) => s + (Number(n[d]) || 0) * x, 0).toFixed(2)); };

  const openTill = async () => { printEndOfDayOpenTill(company); try { await api("POST", "/pos/open-drawer"); } catch { /* non-fatal */ } };
  const tally = async (start, end, view) => {
    if (!start) return toast.error("You have not selected a Start Date");
    if (!end) return toast.error("You have not selected an End Date");
    try { printPosEndOfDay(await api("GET", `/pos/end-of-day?start=${start}&end=${end}`), clerk, company, view); } catch (e) { toast.error(e.message); }
  };
  const finish = async () => {
    if (busy) return;
    if (eod === "") return toast.error("Count the till, or type the End Of Day Cash Drawer Balance.");
    setBusy(true);
    try {
      const denominations = Object.fromEntries(Object.entries(qty).filter(([, v]) => v));
      await api("POST", "/pos/end-of-day", { amount: Number(eod) || 0, denominations });
      printPosEndOfDay(await api("GET", "/pos/end-of-day"), clerk, company);
      sessionStorage.setItem("anx_day_closed", auToday());
      setTimeout(logout, 600);
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  const today = info?.today || {};
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-auto" data-testid="end-of-day-overlay">
      <div className="relative shrink-0 rounded-lg overflow-hidden shadow-2xl border border-black/40" data-testid="end-of-day"
        style={{ width: 1252, height: 640, backgroundImage: "url('/cash-drawer-bg.png')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-black/40" />
        <Ladder qty={qty} setQty={setQty} onLast={() => document.querySelector('[data-testid="eod-balance-input"]')?.focus()} />
        <div style={T(3341, 113, 12300, 900)} className="flex items-center justify-center text-white font-black text-[40px] underline drop-shadow" data-testid="eod-title">END OF DAY {dmy(today.date)}</div>
        <div style={T(15900, 113, 2860, 1400)} className="text-right text-white font-black drop-shadow space-y-1" data-testid="eod-float-box">
          <div className="text-[17px]">TILL START FLOAT</div>
          <div className="text-[17px]">{dmy(today.date)}</div>
          <div className="text-[22px] text-amber-300" data-testid="eod-start-float">{today.started ? money(today.float) : "Not entered"}</div>
        </div>
        <button onClick={onClose} data-testid="eod-close" className="absolute right-3 bottom-3 text-white/80 hover:text-white text-sm font-bold border border-white/40 rounded px-3 py-1">Close</button>
        <Btn pos={T(6504, 1800, 5669, 690)} onClick={openTill} testid="eod-open-till">OPEN TILL FOR END OF DAY CASH COUNT</Btn>
        <div style={T(4669, 3288, 9371, 630)} className="text-center text-white font-black text-[28px] underline drop-shadow" data-testid="eod-balance-label">END OF DAY CASH DRAWER BALANCE</div>
        <input style={T(7370, 4082, 3969, 585)} value={eod} placeholder="0.00" inputMode="decimal" data-testid="eod-balance-input"
          onChange={(e) => { setQtyState({}); setEod(e.target.value.replace(/[^0-9.]/g, "")); }}
          onKeyDown={(e) => { if (e.key === "Enter") finish(); }}
          className="bg-[#fff7b0] border border-black/60 text-[26px] font-black text-center outline-none focus:ring-2 focus:ring-amber-400" />
        <div style={T(5669, 4800, 7371, 1500)} className="text-center text-white font-black text-[15px] underline leading-snug drop-shadow">
          Count each note and coin on the left{denomTotal ? ` (${money(denomTotal)})` : ""}, or simply type the balance, then press 'ENTER' to finish the day.
          {today.end_of_day != null && <div className="text-amber-300 no-underline mt-1" data-testid="eod-already">End of day already saved today: {money(today.end_of_day)}</div>}
        </div>
        <button style={T(7370, 6150, 3969, 600)} onClick={finish} disabled={busy} data-testid="eod-finish"
          className="rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black text-[16px] shadow-lg">{busy ? "Closing…" : "FINISH END OF DAY"}</button>
        <Btn pos={T(3855, 7029, 5384, 572)} onClick={() => tally(auToday(), auToday())} testid="eod-print-day-tally">PRINT DAY TALLY</Btn>
        <Btn pos={T(9467, 7029, 5384, 572)} onClick={() => tally(auToday(), auToday(), true)} testid="eod-view-day-tally">VIEW DAY TALLY</Btn>
        <Btn pos={T(3855, 7766, 5384, 572)} onClick={() => tally(range.start, range.end)} testid="eod-print-range">PRINT DATE RANGE</Btn>
        <Btn pos={T(9467, 7766, 5384, 572)} onClick={() => tally(range.start, range.end, true)} testid="eod-view-range">VIEW DATE RANGE</Btn>
        <div style={T(3116, 8500, 3870, 900)} className="text-white text-[12px] font-bold leading-tight drop-shadow">Select your date range before you press the "Print or View" Date Range Buttons</div>
        {[["start", "Start Date", 7143], ["end", "End Date", 9581]].map(([k, l, x]) => (
          <div key={k} style={T(x, 8480, 2300, 760)}>
            <div className="text-white text-[12px] font-bold">{l}</div>
            <input type="date" value={range[k]} onChange={(e) => setRange({ ...range, [k]: e.target.value })} data-testid={`eod-${k}-date`}
              className="w-full bg-[#ffffcc] border border-black/50 text-[12px] px-1" />
          </div>
        ))}
      </div>
    </div>
  );
}
